import { Link } from "react-router-dom";
import { Lock, ArrowLeft, Bell } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND_LOGOS, COMPANY } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientPortal() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const notify = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: "Portal Waitlist",
        email,
        company: "",
        phone: "",
        message: "Notify me when the Client Portal launches.",
      });
      toast.success("You're on the list. We'll email you at launch.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="client-portal-page"
      className="min-h-screen relative overflow-hidden flex items-center justify-center px-6 py-24 bg-[#0B1120]"
    >
      {/* BG */}
      <div className="absolute inset-0 bg-grain opacity-30" />
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-orange-500/10 blur-[150px]" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/10 blur-[150px]" />

      <div className="relative max-w-2xl w-full">
        <Link
          to="/"
          data-testid="portal-back-link"
          className="inline-flex items-center gap-2 font-tech text-[11px] text-slate-400 hover:text-orange-400 transition-colors mb-10"
        >
          <ArrowLeft size={14} />
          Back to Home
        </Link>

        <div className="tracing-beam p-1">
          <div className="bg-[#0B1120]/90 p-10 md:p-14 text-center">
            <img
              src={BRAND_LOGOS.mastersText}
              alt="Master Ready Services"
              className="h-14 w-auto mx-auto mb-8"
            />

            <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6">
              <div className="absolute inset-0 rounded-full bg-orange-500/20 blur-2xl" />
              <div className="relative w-20 h-20 rounded-full border border-orange-500/40 flex items-center justify-center bg-[#0B1120]">
                <Lock size={28} className="text-orange-400" />
              </div>
            </div>

            <div className="font-tech text-[11px] text-orange-400 mb-3">
              CLIENT PORTAL · COMING SOON
            </div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[0.95]">
              <span className="chrome-text">Your Pipeline.</span>
              <br />
              <span className="ember-text">One Login.</span>
            </h1>

            <p className="mt-6 font-body text-slate-400 max-w-md mx-auto">
              We're building a secure client dashboard with live lead delivery, campaign
              analytics, appointment tracking, and CRM exports. Get notified the moment
              it goes live.
            </p>

            <form onSubmit={notify} className="mt-8 max-w-md mx-auto" data-testid="portal-notify-form">
              <div className="flex items-stretch border border-slate-700 focus-within:border-orange-500/60 transition-colors">
                <input
                  data-testid="portal-email-input"
                  type="email"
                  placeholder="you@yourcompany.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent px-4 py-3 text-sm text-slate-100 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="portal-notify-btn"
                  className="px-5 bg-gradient-to-r from-orange-600 to-red-700 text-white font-tech text-[11px] flex items-center gap-2 disabled:opacity-60"
                >
                  <Bell size={14} />
                  {submitting ? "..." : "Notify Me"}
                </button>
              </div>
            </form>

            <div className="mt-10 pt-8 border-t border-white/5 grid grid-cols-3 gap-4 text-left max-w-md mx-auto">
              {[
                ["Live", "Lead Feeds"],
                ["Real-Time", "Analytics"],
                ["Secure", "CRM Export"],
              ].map((p) => (
                <div key={p[1]}>
                  <div className="font-display text-sm gold-text">{p[0]}</div>
                  <div className="font-tech text-[10px] text-slate-500">{p[1]}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 font-tech text-[10px] text-slate-500">
              Existing clients? Email{" "}
              <a href={`mailto:${COMPANY.email}`} className="text-orange-400 hover:text-orange-300">
                {COMPANY.email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
